import React, { useEffect, useRef, useState } from 'react';
import { CheckCircle2, Compass, PenTool, Code2 } from 'lucide-react';
import './About.css';

/* ─── animated counter ─── */
function useCounter(target, active, duration = 1600) {
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!active) return;
    let cur = 0;
    const step = target / (duration / 16);
    const id = setInterval(() => {
      cur = Math.min(cur + step, target);
      setVal(Math.floor(cur));
      if (cur >= target) clearInterval(id);
    }, 16);
    return () => clearInterval(id);
  }, [active, target, duration]);
  return val;
}

function StatBlock({ target, suffix = '', label, delay = 0, active }) {
  const val = useCounter(target, active);
  return (
    <div className={`about-stat${active ? ' about-in' : ''}`} style={{ '--stagger': `${delay}ms` }}>
      <span className="about-stat-num">{val}{suffix}</span>
      <span className="about-stat-label">{label}</span>
    </div>
  );
}

/* ─── auto-sliding image column ─── */
function ImageSlider({ images, interval = 3500, startDelay = 0 }) {
  const [index, setIndex] = useState(0);

  /* startDelay offsets the first transition so multiple sliders on the
     same page don't advance in lockstep */
  useEffect(() => {
    if (images.length <= 1) return;
    let intervalId;
    const timeoutId = setTimeout(() => {
      setIndex((prev) => (prev + 1) % images.length);
      intervalId = setInterval(() => {
        setIndex((prev) => (prev + 1) % images.length);
      }, interval);
    }, startDelay);

    return () => {
      clearTimeout(timeoutId);
      if (intervalId) clearInterval(intervalId);
    };
  }, [images.length, interval, startDelay]);

  return (
    <div className="about-slider">
      {images.map((src, i) => (
        <img
          key={src + i}
          src={src}
          alt=""
          className={`about-slider-img${i === index ? ' about-slider-active' : ''}`}
        />
      ))}
      <div className="about-slider-dots">
        {images.map((_, i) => (
          <span
            key={i}
            className={`about-slider-dot${i === index ? ' about-slider-dot-active' : ''}`}
          />
        ))}
      </div>
    </div>
  );
}

const pillars = [
  { icon: Compass, title: 'Discover', text: 'We dig into your goals, users, and constraints before writing a single line of code.' },
  { icon: PenTool, title: 'Design', text: 'Interfaces built around clarity, with a brand system that scales alongside you.' },
  { icon: Code2, title: 'Build', text: 'Clean, maintainable code shipped fast, with room to grow.' },
];

/* replace these with your own image paths */
const leftImages = [
  '/heroo.jpg',
  '/development.png',
  '/projects.png',
];

const rightImages = [
  '/sample.png',
  '/Portfolio (5).png',
  '/sample2.png',
];

/* ═══════════════════════════════════════════
   ABOUT
═══════════════════════════════════════════ */
export default function About() {
  const sectionRef = useRef(null);
  const [inView, setInView] = useState(false);

  /* entrance-only reveal */
  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setInView(true);
            obs.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.25 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <section className="about" id="about" ref={sectionRef}>
      <div className="about-bg">
        <div className="about-orb about-orb-1" />
        <div className="about-orb about-orb-2" />
      </div>

      <div className="about-inner">
        {/* ── left copy ── */}
        <div className={`about-copy${inView ? ' about-in' : ''}`}>
          <div className="about-badge">
            <span className="about-badge-dot" />
            About Maxwell Tech
          </div>

          <h2 className="about-title">
            We Don't Just Ship Code.<br />
            <span className="gradient-text">We Build Momentum.</span>
          </h2>

          <p className="about-text">
            Maxwell Tech is a freelance development team helping small businesses and startups turn
            ideas into fast, reliable digital products — from custom web apps to full-scale platforms,
            built with the same care we'd want for our own.
          </p>

          <ul className="about-list">
            <li><CheckCircle2 size={18} /> Full-stack execution, start to finish</li>
            <li><CheckCircle2 size={18} /> Transparent process, no black boxes</li>
            <li><CheckCircle2 size={18} /> Built to scale with your business</li>
          </ul>

          <div className="about-stats">
            <StatBlock target={30} suffix="+" label="Projects Shipped" delay={0} active={inView} />
            <StatBlock target={98} suffix="%" label="Client Satisfaction" delay={120} active={inView} />
            <StatBlock target={4} suffix="+" label="Years Building" delay={240} active={inView} />
            <StatBlock target={12} suffix="+" label="Technologies" delay={360} active={inView} />
          </div>
        </div>

        {/* ── right visual — stacked pillar cards ── */}
        <div className={`about-visual${inView ? ' about-in' : ''}`}>
          {pillars.map(({ icon: Icon, title, text }, i) => (
            <div
              key={title}
              className="about-pillar-card"
              style={{
                '--stagger': `${220 + i * 140}ms`,
                '--rot': `${i === 0 ? -4 : i === 1 ? 2 : -2}deg`,
              }}
            >
              <div className="about-pillar-icon"><Icon size={20} /></div>
              <span className="about-pillar-title">{title}</span>
              <p className="about-pillar-text">{text}</p>
            </div>
          ))}
        </div>
      </div>

      {/* ── bottom gallery — two 50/50 auto-sliding image columns, no gap, rectangular ── */}
      <div className={`about-gallery${inView ? ' about-in' : ''}`}>
        <ImageSlider images={leftImages} interval={3500} startDelay={0} />
        <ImageSlider images={rightImages} interval={4200} startDelay={1600} />
      </div>
    </section>
  );
}